import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gauge, AlertTriangle } from "lucide-react";
import { DriverShell } from "@/components/shells";
import { Panel, Spinner, Btn, Field, Input } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { loadDriverContext } from "@/lib/driverData";
import { api } from "@/lib/api";
import { formatReg } from "@/lib/vda";

// Mandatory mileage capture immediately after OTP (Round 2 #2). Drivers become
// used to having current mileage ready when opening Relay. Session-flagged so we
// never re-prompt in the same session once supplied.
export function mileageCapturedKey(vehicleId) { return `relay_mileage_${vehicleId}`; }

export default function DriverMileageIntake() {
  const nav = useNavigate();
  const { data: ctx, loading } = useAsync(() => loadDriverContext(), []);
  const [mileage, setMileage] = useState("");
  const [saving, setSaving] = useState(false);

  const baseline = ctx?.vehicle?.current_mileage ?? null;
  const warning = useMemo(() => {
    if (!mileage || baseline == null) return null;
    const m = Number(mileage);
    if (m < baseline) return `That's lower than our last reading (${baseline.toLocaleString()}). Please double-check.`;
    if (m - baseline > 50000) return `That's a large jump from ${baseline.toLocaleString()}. Please double-check.`;
    return null;
  }, [mileage, baseline]);

  if (loading) return <div className="min-h-screen grid place-items-center"><Spinner /></div>;

  // No active vehicle → nothing to capture; continue to home.
  if (!ctx?.vehicle?.id) { nav("/driver", { replace: true }); return null; }

  const proceed = async () => {
    if (!mileage) { toast.error("Please enter the current mileage to continue."); return; }
    setSaving(true);
    try {
      await api.driverMileage({ mileage: Number(mileage), vehicle_id: ctx.vehicle.id, source: "driver_checkin" });
      try { sessionStorage.setItem(mileageCapturedKey(ctx.vehicle.id), "1"); } catch (_) { /* ignore */ }
      toast.success("Thanks — mileage recorded.");
      nav("/driver", { replace: true });
    } catch (e) {
      toast.error(e.message || "Could not save mileage. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <DriverShell title="Current mileage">
      <Panel className="p-3 mb-4 text-sm text-muted-foreground">
        Before you continue, confirm the mileage on <span className="vda-reg text-foreground">{formatReg(ctx.vehicle.registration)}</span>. This keeps your records accurate and speeds up any booking.
      </Panel>
      <div className="space-y-4">
        <Field label="Odometer reading (miles)">
          <Input data-testid="mileage-intake-input" aria-label="Odometer reading in miles" inputMode="numeric" autoFocus value={mileage}
            onChange={(e) => setMileage(e.target.value.replace(/\D/g, ""))} placeholder="e.g. 42000" className="text-2xl h-14 vda-reg" />
        </Field>
        {baseline != null && <p className="text-xs text-muted-foreground -mt-2">Last recorded: {baseline.toLocaleString()} mi</p>}
        {warning && (
          <Panel className="p-3 text-sm bg-[hsl(var(--vda-amber-bg))] border-[hsl(var(--vda-amber))]/30 flex gap-2" data-testid="mileage-intake-warning">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-[hsl(var(--vda-amber))] flex-none" />
            <span className="text-foreground/80">{warning} We'll still record it.</span>
          </Panel>
        )}
        <Btn data-testid="mileage-intake-submit" onClick={proceed} loading={saving} className="w-full h-12">
          <Gauge className="h-4 w-4" /> Confirm mileage & continue
        </Btn>
      </div>
    </DriverShell>
  );
}
